import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ProjectInfo } from "./ProjectInfo";
import { ImageInfo } from "./ImageInfo";
import { extractImagesAtPath } from "./Helpers";

export const useMeasureElement = (ref: HTMLElement | undefined | null, zoom: number) => {
  const [getSizeInfo, setSizeInfo] = useState<{
    width: number | undefined;
    height: number | undefined;
    scrollWidth: number | undefined;
    scrollHeight: number | undefined;
    clientWidth: number | undefined;
    clientHeight: number | undefined;
    offsetWidth: number | undefined;
    offsetHeight: number | undefined;
    isOverflowH: boolean | undefined;
    isOverflowW: boolean | undefined;
  }>({
    width: undefined,
    height: undefined,
    scrollWidth: undefined,
    scrollHeight: undefined,
    clientWidth: undefined,
    clientHeight: undefined,
    offsetWidth: undefined,
    offsetHeight: undefined,
    isOverflowH: undefined,
    isOverflowW: undefined,
  });

  useLayoutEffect(() => {
    const element = ref;

    if (!element) return; // Exit if the ref is not attached to an element

    const checkOverflow = () => {
      const hOverflow = element.scrollHeight > element.offsetHeight;
      const wOverflow = element.scrollWidth > element.offsetWidth;
      if (
        getSizeInfo.isOverflowW !== wOverflow ||
        getSizeInfo.isOverflowH !== hOverflow ||
        getSizeInfo.scrollWidth !== element.scrollWidth ||
        getSizeInfo.scrollHeight !== element.scrollHeight ||
        getSizeInfo.clientWidth !== element.clientWidth ||
        getSizeInfo.clientHeight !== element.clientHeight ||
        getSizeInfo.offsetWidth !== element.offsetWidth ||
        getSizeInfo.offsetHeight !== element.offsetHeight ||
        getSizeInfo.width !== element.offsetWidth ||
        getSizeInfo.height !== element.offsetHeight
      ) {
        setSizeInfo({
          width: element.offsetWidth,
          height: element.offsetHeight,
          scrollWidth: element.scrollWidth,
          scrollHeight: element.scrollHeight,
          clientWidth: element.clientWidth,
          clientHeight: element.clientHeight,
          offsetWidth: element.offsetWidth,
          offsetHeight: element.offsetHeight,
          isOverflowH: hOverflow,
          isOverflowW: wOverflow,
        });
      }
    };

    checkOverflow(); // Check immediately on effect run

    if (window.ResizeObserver) {
      const resizeObserver = new ResizeObserver(checkOverflow);
      resizeObserver.observe(element);

      return () => resizeObserver.disconnect(); // Cleanup function to disconnect observer
    }
  }, [
    getSizeInfo.clientHeight,
    getSizeInfo.clientWidth,
    getSizeInfo.height,
    getSizeInfo.isOverflowH,
    getSizeInfo.isOverflowW,
    getSizeInfo.offsetHeight,
    getSizeInfo.offsetWidth,
    getSizeInfo.scrollHeight,
    getSizeInfo.scrollWidth,
    getSizeInfo.width,
    ref,
    zoom,
  ]);

  return { getSizeInfo };
};

export const useEffectDebugger = (effectHook: any, dependencies: any, dependencyNames = []) => {
  const previousDeps = usePrevious(dependencies, []);

  const changedDeps = dependencies.reduce((accum: any, dependency: any, index: any) => {
    if (dependency !== previousDeps[index]) {
      const keyName = dependencyNames[index] || index;
      return {
        ...accum,
        [keyName]: {
          before: previousDeps[index],
          after: dependency,
        },
      };
    }

    return accum;
  }, {});

  if (Object.keys(changedDeps).length) {
    console.log("[use-effect-debugger] ", changedDeps);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(effectHook, dependencies);
};

const usePrevious = (value: any, initialValue: any) => {
  const ref = useRef(initialValue);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export const useCallbackDebugger = (callbackFn: any, dependencies: any, dependencyNames: string[] = []) => {
  const previousDepsRef = useRef(dependencies);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const callback = useCallback(callbackFn, dependencies);

  const changedDeps = dependencies.reduce((accum: any, dependency: any, index: any) => {
    if (dependency !== previousDepsRef.current[index]) {
      const keyName = dependencyNames[index] || `Dependency ${index}`;
      accum[keyName] = {
        before: previousDepsRef.current[index],
        after: dependency,
      };
    }
    return accum;
  }, {});

  if (Object.keys(changedDeps).length > 0) {
    console.log("[useCallback-debugger] Callback dependencies changed:", changedDeps);
  }

  // Update the ref after comparing current dependencies
  previousDepsRef.current = dependencies;

  return callback;
};

export function useMemoDebugger<T>(valueFactory: () => T, dependencies: any[], dependencyNames: string[] = []): T {
  const previousDeps = usePrevious(dependencies, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedValue = useMemo(valueFactory, dependencies);

  useEffect(() => {
    const changedDeps = dependencies.reduce((acc: Record<string, { before: any; after: any }>, dep, index) => {
      if (previousDeps && dep !== previousDeps[index]) {
        const keyName = dependencyNames[index] || index;
        acc[keyName] = { before: previousDeps[index], after: dep };
      }
      return acc;
    }, {});

    if (Object.keys(changedDeps).length > 0) {
      console.log("[useMemoDebugger] Dependencies changed:", changedDeps);
    }
  }, [dependencies, dependencyNames, memoizedValue, previousDeps]); // React might show a warning about missing dependencies, you can safely ignore it or add them if it makes sense in your case.

  return memoizedValue;
}

export const useObjectImages = (
  modalContextKey: string[],
  treeData: object,
  projectName: string,
  projectInfo: ProjectInfo | undefined,
) => {
  const [possibleBackgroundImages, setPossibleBackgroundImages] = useState<ImageInfo[]>([]);
  const [selectedBackgroundImage, setSelectedBackgroundImage] = useState("");

  useEffect(() => {
    let possibleImages = extractImagesAtPath(modalContextKey[0], treeData, projectName, projectInfo);
    setPossibleBackgroundImages(possibleImages);
    if (possibleImages.length > 0) {
      setSelectedBackgroundImage(possibleImages[0].type === "src" ? possibleImages[0].value : possibleImages[0].name);
    }
  }, [modalContextKey, treeData, projectName, projectInfo]);

  return { possibleBackgroundImages, selectedBackgroundImage, setSelectedBackgroundImage };
};
